import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import type { EmailLista } from '../../../types/email.types';

interface ListaDialogProps {
  aperta: boolean;
  /** Se c'e', si rinomina questa lista; se no, se ne crea una nuova. */
  lista: EmailLista | null;
  onChiudi: () => void;
  onSalva: (nome: string, descrizione: string) => Promise<void>;
}

/** Crea una lista nuova o cambia nome e descrizione a una esistente. */
export function ListaDialog({ aperta, lista, onChiudi, onSalva }: ListaDialogProps) {
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aperta) return;
    setNome(lista?.name ?? '');
    setDescrizione(lista?.description ?? '');
  }, [aperta, lista]);

  const salva = async () => {
    if (!nome.trim()) return;
    setSalvando(true);
    try {
      await onSalva(nome, descrizione);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={aperta} onOpenChange={(v) => !v && onChiudi()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{lista ? 'Modifica la lista' : 'Nuova lista'}</DialogTitle>
          <DialogDescription>
            Una lista raggruppa dei contatti per mandare una campagna solo a loro. Un contatto puo'
            stare in piu' liste.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3 py-2"
          onSubmit={(e) => {
            e.preventDefault();
            salva();
          }}
        >
          <div>
            <Label htmlFor="nome-lista" className="text-xs">
              Nome <span className="text-primary-600">*</span>
            </Label>
            <Input
              id="nome-lista"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Es. Clienti corsi sicurezza, Fiera 2026…"
              className="mt-1"
              autoFocus
            />
            <p className="text-[11px] text-neutral-400 mt-1">
              Lo vedi solo tu: i contatti non sanno in quali liste sono.
            </p>
          </div>
          <div>
            <Label htmlFor="descrizione-lista" className="text-xs">
              A cosa serve
            </Label>
            <Input
              id="descrizione-lista"
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              placeholder="Facoltativo: un promemoria per chi la usera'"
              className="mt-1"
            />
          </div>
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} disabled={salvando}>
            Annulla
          </Button>
          <Button
            onClick={salva}
            disabled={!nome.trim() || salvando}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {lista ? 'Salva' : 'Crea la lista'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
